import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Section from '../common/Section';
import WorkCard from '../common/WorkCard';
import ParallaxText from '../ui/ParallaxText';
import { PROJECTS_DATA } from '../../utils/constants';
import { Filter } from 'lucide-react';

const Works: React.FC = () => {
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [hoveredId, setHoveredId] = useState<string | null>(null);

    const categories = [
        { id: 'all', label: 'All Projects' },
        { id: 'app', label: 'App Design' },
        { id: 'web', label: 'Web Platform' },
        { id: 'community', label: 'Community' },
    ];

    const filteredProjects = selectedCategory === 'all'
        ? PROJECTS_DATA
        : PROJECTS_DATA.filter(project => {
            if (selectedCategory === 'app') return project.id === 'vrci';
            if (selectedCategory === 'web') return project.id === 'xrcc';
            if (selectedCategory === 'community') return ['sakuragaoka', 'video'].includes(project.id);
            return true;
        });

    return (
        <Section id="works" className="relative bg-gradient-to-b from-sky-50/50 to-white">
            {/* Background Pattern */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(14, 165, 233, 0.1) 35px, rgba(14, 165, 233, 0.1) 70px)`,
                    }} />
                </div>
            </div>

            <div className="relative z-10">
                <ParallaxText offset={30}>
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="h2-display text-slate-900 mb-4">
                            <span className="text-gradient">Featured Works</span>
                        </h2>
                        <div className="w-24 h-1 mx-auto bg-gradient-to-r from-sky-400 to-purple-400 rounded-full mt-6" />
                    </motion.div>
                </ParallaxText>

                {/* Filter Buttons */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="flex flex-wrap justify-center gap-4 mb-12"
                >
                    <div className="flex items-center gap-2 text-slate-600 mr-4">
                        <Filter size={20} />
                        <span className="text-sm font-medium">Filter:</span>
                    </div>
                    {categories.map((category) => (
                        <motion.button
                            key={category.id}
                            onClick={() => setSelectedCategory(category.id)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                                selectedCategory === category.id
                                    ? 'bg-gradient-to-r from-sky-400 to-sky-500 text-white shadow-lg shadow-sky-500/25'
                                    : 'bg-white text-slate-600 hover:bg-sky-50 border border-slate-200'
                            }`}
                        >
                            {category.label}
                        </motion.button>
                    ))}
                </motion.div>

                {/* Projects Grid */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={selectedCategory}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                        className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8"
                    >
                        {filteredProjects.map((project, index) => (
                            <motion.div
                                key={project.id}
                                layout
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                                onMouseEnter={() => setHoveredId(project.id)}
                                onMouseLeave={() => setHoveredId(null)}
                                className="relative"
                            >
                                {/* Hover Glow Effect */}
                                <AnimatePresence>
                                    {hoveredId === project.id && (
                                        <motion.div
                                            className="absolute inset-0 bg-gradient-to-br from-sky-400/10 to-purple-400/10 rounded-xl filter blur-2xl"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.3 }}
                                        />
                                    )}
                                </AnimatePresence>

                                <WorkCard project={project} index={index} />
                            </motion.div>
                        ))}
                    </motion.div>
                </AnimatePresence>

                {/* Call to Action */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mt-16"
                >
                    <p className="text-slate-600 mb-6">
                        Interested in collaborating on your next project?
                    </p>
                    <motion.a
                        href="#contact"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn-primary inline-flex items-center gap-2"
                    >
                        Let's Work Together
                        <motion.span
                            animate={{ x: [0, 5, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                        >
                            →
                        </motion.span>
                    </motion.a>
                </motion.div>
            </div>
        </Section>
    );
};

export default Works;